import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiDatabase,
  FiFolder,
  FiFolderPlus,
  FiHelpCircle,
  FiMaximize2,
  FiMenu,
  FiMoon,
  FiMoreHorizontal,
  FiRefreshCw,
  FiSearch,
  FiSettings,
  FiSun,
  FiX,
} from 'react-icons/fi';
import { useAppStore } from '../../store/useAppStore';
import { IconButton } from '../../components/IconButton';
import { sortOptions } from '../library/libraryUtils';

export function AppToolbar({ onRefresh }: { onRefresh: () => void }) {
  const [more, setMore] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);
  const searchInput = useRef<HTMLInputElement>(null);
  const query = useAppStore((state) => state.query);
  const sort = useAppStore((state) => state.sort);
  const theme = useAppStore((state) => state.settings.theme);
  const addItem = useAppStore((state) => state.addItem);
  const setQuery = useAppStore((state) => state.setQuery);
  const setSort = useAppStore((state) => state.setSort);
  const setFullscreen = useAppStore((state) => state.setFullscreen);
  const setSettingsOpen = useAppStore((state) => state.setSettingsOpen);
  const setOutlineOpen = useAppStore((state) => state.setOutlineOpen);
  const cycleTheme = useAppStore((state) => state.cycleTheme);
  useEffect(() => {
    if (mobileSearch) searchInput.current?.focus();
  }, [mobileSearch]);
  const open = (file?: File) => {
    if (!file || file.type !== 'application/pdf') return;
    addItem({
      id: crypto.randomUUID(),
      name: file.name,
      size: file.size,
      pages: 1,
      modifiedAt: new Date(file.lastModified).toISOString(),
      url: URL.createObjectURL(file),
      recent: true,
      local: true,
    });
  };
  return (
    <header className="app-toolbar">
      <div className="brand">
        <div className="brand-mark">P</div>
        <div>
          <strong>Paperflow</strong>
          <span>Reader</span>
        </div>
      </div>
      <IconButton
        label="Document navigation"
        onClick={() => setOutlineOpen(true)}
        className="desktop-only"
      >
        <FiMenu />
      </IconButton>
      <IconButton label="Search documents" active={mobileSearch || Boolean(query)} className="mobile-only mobile-search-trigger" onClick={() => { setMore(false); setMobileSearch((value) => !value); }}>
        <FiSearch />
      </IconButton>
      <div className={`search ${mobileSearch ? 'mobile-search-open' : ''}`}>
        <FiSearch />
        <input
          ref={searchInput}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search documents…"
          aria-label="Search documents"
        />
        {query && <IconButton label="Clear search" className="search-clear" onClick={() => { setQuery(''); searchInput.current?.focus(); }}><FiX /></IconButton>}
        <kbd>⌘ K</kbd>
      </div>
      <select
        value={sort}
        onChange={(event) => setSort(event.target.value as typeof sort)}
        aria-label="Sort library"
        className="sort-select"
      >
        {sortOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <IconButton label="Document navigation" onClick={() => setOutlineOpen(true)} className="mobile-only mobile-primary-action">
        <FiMenu />
      </IconButton>
      <IconButton label="Settings" onClick={() => setSettingsOpen(true)} className="mobile-only mobile-primary-action">
        <FiSettings />
      </IconButton>
      <div className="toolbar-actions">
        <label className="icon-button open-button" title="Open local PDF">
          <FiFolderPlus />
          <input
            type="file"
            accept="application/pdf"
            onChange={(event) => open(event.target.files?.[0])}
          />
        </label>
        <IconButton label="Refresh library" onClick={onRefresh} className="desktop-only">
          <FiRefreshCw />
        </IconButton>
        <IconButton label={`Theme: ${theme}`} onClick={cycleTheme} className="desktop-only">
          {theme === 'dark' ? <FiMoon /> : <FiSun />}
        </IconButton>
        <IconButton
          label="Fullscreen reader"
          onClick={() => setFullscreen(true)}
          className="desktop-only"
        >
          <FiMaximize2 />
        </IconButton>
        <Link
          to="/manage"
          className="icon-button desktop-only"
          aria-label="PDF Manager"
          title="PDF Manager"
        >
          <FiDatabase />
        </Link>
        <Link
          to="/libraries"
          className="icon-button desktop-only"
          aria-label="Manage libraries"
          title="Manage libraries"
        >
          <FiFolder />
        </Link>
        <Link
          to="/guide"
          className="icon-button desktop-only"
          aria-label="Guide"
          title="Guide"
        >
          <FiHelpCircle />
        </Link>
        <IconButton label="Settings" onClick={() => setSettingsOpen(true)} className="desktop-only">
          <FiSettings />
        </IconButton>
        <IconButton label="More actions" className="mobile-only" onClick={() => { setMobileSearch(false); setMore(!more); }}>
          <FiMoreHorizontal />
        </IconButton>
      </div>
      {more && (
        <div className="mobile-menu">
          <label className="mobile-menu-file">
            <FiFolderPlus /> Open PDF
            <input type="file" accept="application/pdf" onChange={(event) => { open(event.target.files?.[0]); setMore(false); }} />
          </label>
          <button onClick={cycleTheme}>
            <FiSun /> Theme: {theme}
          </button>
          <button onClick={() => setFullscreen(true)}>
            <FiMaximize2 /> Fullscreen
          </button>
          <button onClick={() => { onRefresh(); setMore(false); }}>
            <FiRefreshCw /> Sync libraries
          </button>
          <Link to="/manage">
            <FiDatabase /> PDF Manager
          </Link>
          <Link to="/libraries">
            <FiFolder /> Manage libraries
          </Link>
          <Link to="/guide">
            <FiHelpCircle /> Guide
          </Link>
        </div>
      )}
    </header>
  );
}
